import { ChildNotebookDomainService } from './domain';
import { PageFactory } from './entity/pageFactory';

import type { PageIRepository } from './entity/pageIRepository';
import type { DeleteOutputType, EntitySubscribeType } from '@/infra/appsync';
import type { Logger } from '@/infra/logger';
import type { WagakoNurseryIApi } from '@/infra/wagakoNurseryIApi';
import type { PageJson, PageJsonPvt } from '@/types/domain/childNotebook/entities/pageEntity';

/**
 * 連絡帳アプリケーションサービス
 */
export class ChildNotebookApplicationService {
  private service!: ChildNotebookDomainService;

  private pageFactory = new PageFactory();

  /**
   * Injection
   * @param pageRepository pageRepository
   * @param wagakoNurseryApi wagakoNurseryApi
   * @param logger logger
   */
  public inject(
    pageRepository: PageIRepository,
    wagakoNurseryApi: WagakoNurseryIApi,
    logger: Logger,
  ) {
    this.service = new ChildNotebookDomainService(logger);
    this.service.inject(pageRepository, wagakoNurseryApi);
  }

  /**
   * ページリスト取得
   * @param date 日付
   * @returns リスト
   */
  async getPageList(date: string) {
    const result = await this.service.getPageList(date);
    const items = result.map((v) => v.toJson());
    return items;
  }

  /**
   * ページ作成
   * @param input 入力
   * @returns 結果
   */
  async createPage(
    input: PageJson,
  ) {
    const entity = this.pageFactory.createFromJson(input);
    const result = await this.service.createPage(entity);
    return result;
  }

  /**
   * ページ更新
   * @param input 入力
   * @returns 結果
   */
  async updatePage(
    input: PageJson,
  ) {
    const entity = this.pageFactory.createFromJson(input);
    const result = await this.service.updatePage(entity);
    return result;
  }

  /**
   * 体温削除
   * @param input 入力
   * @returns 結果
   */
  async deletePage(
    input: PageJson,
  ) {
    const entity = this.pageFactory.createFromJson(input);
    const result = await this.service.deletePage(entity);
    return result;
  }

  /**
   * ページリストを更新する(Create)
   * @param pageList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public createPageStore(
    pageList: PageJson[],
    payload: EntitySubscribeType,
    filter: ((json: PageJsonPvt) => boolean) | undefined,
  ) {
    return this.service.createEntityStorePub(
      pageList,
      payload,
      this.pageFactory,
      filter,
    );
  }

  /**
   * ページリストを更新する(Update)
   * @param pageList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public updatePageStore(
    pageList: PageJson[],
    payload: EntitySubscribeType,
  ) {
    return this.service.updateEntityStorePub(
      pageList,
      payload,
      this.pageFactory,
    );
  }

  /**
   * ページリストを更新する(Delete)
   * @param pageList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public deletePageStore(
    pageList: PageJson[],
    payload: DeleteOutputType,
  ) {
    return this.service.deleteEntityStorePub(
      pageList,
      payload,
      this.pageFactory,
    );
  }

  /**
   * アップロードするファイルの抽出
   * @param files File[]
   * @returns 結果
   */
  async extractFiles(
    input: PageJson,
    files: File[],
  ) {
    const images: File[] = [];
    const attachments: File[] = [];
    for (let i = 0; i < files.length; i += 1) {
      const { name } = files[i];
      let { type } = files[i];
      if (type === '') {
        const tmp = name.split('.');
        if (tmp[tmp.length - 1].toLowerCase() === 'heic') {
          type = 'image/heic';
        }
      }
      if (type === 'image/jpeg' || type === 'image/png' || type === 'image/heic') {
        images.push(files[i]);
      } else {
        attachments.push(files[i]);
      }
    }
    const page = { ...input };
    // 保存時のファイル名をここで決定
    page.data.images = images.map((v) => `${v.name.split('.').slice(0, -1).join('.')}.webp`);
    page.data.attachments = attachments.map((v) => v.name);
    return {
      /** ページ */
      page,
      /** 画像 */
      images,
      /** 添付 */
      attachments,
    };
  }

  /**
   * sendNurseryPage
   * @param input input
   * @param images File[]
   * @param attachments File[]
   * @returns 結果
   */
  async sendNurseryPage(
    input: PageJson,
    images: File[],
    attachments: File[],
  ) {
    // Entity作成
    const entity = this.pageFactory.createFromJson(input);
    await this.service.sendNurseryPage(entity);
    // アップロード処理
    {
      const result = await this.service.saveImages(images, entity);
      if (result.includes(false)) {
        return false;
      }
    }
    {
      const result = await this.service.saveAttachments(attachments, entity);
      if (result.includes(false)) {
        return false;
      }
    }
    return true;
  }

  /**
   * removeNurseryPage
   * @param input input
   */
  async removeNurseryPage(
    input: PageJson,
  ) {
    const entity = this.pageFactory.createFromJson(input);
    // ファイル削除処理
    await this.service.removeFiles(entity);
    // Entity削除
    await this.service.removeNurseryPage(entity);
  }

  /**
   * 既読
   * @param input 入力
   */
  async alreadyRead(
    input: PageJson,
  ) {
    const entity = this.pageFactory.createFromJson(input);
    await this.service.alreadyRead(entity);
  }
}
